"use client";

import { AnimatePresence, m } from "motion/react";
import { useState } from "react";
import Comparison from "@/components/preview/comparison";
import DemoCheckbox from "@/components/preview/demo-checkbox";
import { SPRING_FAST } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { MeasurementBadge, MeasurementBand } from "../measurement";

const OPTICAL_NUDGE = 6;

const POP = { filter: "blur(4px)", opacity: 0, scale: 0.5 };
const REST = { filter: "blur(0px)", opacity: 1, scale: 1 };

function PlaySvg({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={cn(
        "size-14 shrink-0 fill-gray-900 will-change-transform",
        className
      )}
      fill="none"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M7.97943 2.53439C5.98076 1.29992 3.40295 2.73762 3.40295 5.08679V18.9147C3.40295 21.2639 5.98076 22.7016 7.97943 21.4671L19.1735 14.5531C21.0716 13.3808 21.0716 10.6206 19.1735 9.44837L7.97943 2.53439Z" />
    </svg>
  );
}

function PauseSvg() {
  return (
    <svg
      aria-hidden="true"
      className="size-14 shrink-0 fill-gray-900 will-change-transform"
      fill="none"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M4 6C4 4.34315 5.34315 3 7 3C8.65685 3 10 4.34315 10 6V18C10 19.6569 8.65685 21 7 21C5.34315 21 4 19.6569 4 18V6Z" />
      <path d="M14 6C14 4.34315 15.3431 3 17 3C18.6569 3 20 4.34315 20 6V18C20 19.6569 18.6569 21 17 21C15.3431 21 14 19.6569 14 18V6Z" />
    </svg>
  );
}

function PlayButton({
  onToggle,
  optical,
  paused,
  showMargin,
}: {
  onToggle: () => void;
  optical: boolean;
  paused: boolean;
  showMargin: boolean;
}) {
  const nudge = optical && paused ? OPTICAL_NUDGE : 0;

  return (
    <div className="relative flex items-center justify-center">
      <MeasurementBadge
        className="top-1/2 -left-16 -translate-y-1/2"
        show={showMargin}
      >
        {nudge}px
      </MeasurementBadge>
      <MeasurementBadge
        className="top-1/2 -right-16 -translate-y-1/2"
        show={showMargin}
      >
        0px
      </MeasurementBadge>
      <m.button
        aria-label={paused ? "Play" : "Pause"}
        className="relative flex size-24 items-center justify-center overflow-hidden rounded-full bg-gray-300"
        initial={false}
        onClick={onToggle}
        transition={SPRING_FAST}
        whileTap={{ scale: 0.95 }}
      >
        <MeasurementBand
          className="inset-y-0 left-0"
          show={showMargin && nudge > 0}
          style={{ marginLeft: "calc(50% - 28px)", width: nudge }}
        />
        <AnimatePresence initial={false} mode="popLayout">
          <m.div
            animate={REST}
            className="flex will-change-transform"
            exit={POP}
            initial={POP}
            key={paused ? "play" : "pause"}
            transition={SPRING_FAST}
          >
            {paused ? (
              <PlaySvg className={cn(optical && "ml-1.5")} />
            ) : (
              <PauseSvg />
            )}
          </m.div>
        </AnimatePresence>
      </m.button>
    </div>
  );
}

export default function UiOpticalAlignmentDemo() {
  const [showMargin, setShowMargin] = useState(false);
  const [paused, setPaused] = useState(true);

  const toggle = () => setPaused(!paused);

  return (
    <Comparison
      bad={
        <PlayButton
          onToggle={toggle}
          optical={false}
          paused={paused}
          showMargin={showMargin}
        />
      }
      footer={
        <DemoCheckbox
          checked={showMargin}
          label="Show margin"
          onCheckedChange={setShowMargin}
        />
      }
      good={
        <PlayButton
          onToggle={toggle}
          optical
          paused={paused}
          showMargin={showMargin}
        />
      }
      panelClassName="h-52"
      stackBelow="sm"
    />
  );
}
